/**
 * FinderlyFix Contractor Fee Schedule — the written fee agreement Partner
 * Terms §7 refers to ("Any fees are set out in a separate order form or
 * written agreement"). An owner or admin accepts it on the Billing screen
 * before any fee posts (`POST /partner/billing/fee-schedule/accept`, recorded
 * in `partner_fee_schedule_acceptances`). The Partner Terms are NOT bumped for
 * it: this schedule is that separate agreement.
 *
 * Final as of 2026-10-19 (owner decision): the commercial terms (lead fee on
 * accept, default $45; 30% success fee on confirmed completion; 7-day
 * auto-confirm; 30-day disputes; monthly invoices, net 15; 30 days' notice
 * of price increases; off-platform circumvention may be charged on the
 * accepted value and the page suspended) and this wording are the version
 * partners accept. Never edit the text under this version: a change is a
 * new `contractor-fees/YYYY-MM-DD` that every enrolled organisation
 * re-accepts before new fees post.
 *
 * The numbers here mirror the server's defaults and summary
 * (server/src/constants/contractor-fee-schedule.ts
 * `CONTRACTOR_FEE_SCHEDULE_SUMMARY`; a server test pins both the version and
 * these figures). The price an organisation actually pays per lead is the one
 * shown when it accepts that lead, which an operator may set per partner and
 * per trade.
 *
 * NEVER EDIT THE TEXT UNDER A SHIPPED VERSION. Bump
 * `CONTRACTOR_FEE_SCHEDULE_VERSION` here AND in
 * server/src/constants/contractor-fee-schedule.ts (the parity test in
 * server/tests/unit/partner-terms-version.test.ts reads this file). A bump
 * makes every enrolled organisation accept again before new fees post.
 */
import type { LegalDocument } from './model';

export const CONTRACTOR_FEE_SCHEDULE_VERSION = 'contractor-fees/2026-10-19';

export const CONTRACTOR_FEE_SCHEDULE_PATH = '/legal/contractor-fee-schedule';

export const CONTRACTOR_FEE_SCHEDULE_DOCUMENT: LegalDocument = {
  path: CONTRACTOR_FEE_SCHEDULE_PATH,
  title: 'Contractor Fee Schedule',
  version: CONTRACTOR_FEE_SCHEDULE_VERSION,
  updated: 'October 19, 2026',
  intro: [
    'This Contractor Fee Schedule (the “Schedule”) is an agreement between AI Vision Technologies Inc, a Texas corporation doing business as FinderlyFix (“FinderlyFix”, “we”, “us”), and the organisation that accepts it in the FinderlyFix partner portal (“you”). It takes effect for your organisation when it is accepted, and applies to leads and quotes from then on.',
  ],
  sections: [
    {
      id: 'scope',
      heading: '1. Who this applies to',
      blocks: [
        'This Schedule applies to organisations that use the FinderlyFix-hosted contractor page and the pay-per-lead and quote tools in the portal. It is the separate written agreement about fees that section 7 of the FinderlyFix Partner Terms refers to.',
        'An owner or admin accepts this Schedule in the portal on your organisation’s behalf, and that acceptance binds your organisation. We do not charge any fee under this Schedule until it has been accepted.',
      ],
    },
    {
      id: 'lead-fee',
      heading: '2. Lead fee',
      blocks: [
        'We charge a lead fee once per lead, and only when you accept the lead, in the portal or through a lead link, and are shown the homeowner’s full contact details. Before you accept, you see the job and the homeowner’s general area, not their contact details.',
        'The lead fee is the price shown to you when you accept. The default is $45 per lead; the price may differ by trade, and the portal always shows it before you accept.',
        'You pay nothing for a lead you decline, ignore or let expire, a request that was not matched to you, or a test lead.',
        'If you accepted a lead by mistake, or the lead is not genuine (for example, the contact details are fake or the homeowner never asked for help), you can dispute the fee within 30 days under section 6.',
      ],
    },
    {
      id: 'success-fee',
      heading: '3. Success fee',
      blocks: [
        'When a homeowner accepts your quote and the project is completed, we charge a success fee of 30% of the accepted quote value. If we resolve a dispute about the project by setting an adjusted value, the fee is 30% of that adjusted value instead.',
        'We charge the success fee once per project, when completion is confirmed in one of these ways:',
        {
          list: [
            'the homeowner confirms completion;',
            'you report completion and the homeowner does nothing for 7 days, in which case completion is confirmed automatically; or',
            'FinderlyFix confirms completion after reviewing a dispute.',
          ],
        },
        'Your quote prices are yours to set. They should account for this fee: the portal shows the fee and what you keep before you send a quote.',
        'We charge no success fee on a project that is cancelled, and none while a dispute about the project is open.',
      ],
    },
    {
      id: 'quotes',
      heading: '4. Quotes and completion',
      blocks: [
        'You send quotes to the homeowner through the portal, after you have accepted the lead. We email the homeowner a link to your quote, and the homeowner accepts or declines it from that link. An accepted quote becomes a project.',
        'When the work is done, you report completion in the portal. We then ask the homeowner to confirm. If the homeowner confirms, or does nothing for 7 days, the project is complete and the success fee is charged. If the homeowner says something is wrong, the project is disputed and FinderlyFix reviews it before any success fee is charged. We may ask both of you for information, and we decide whether the project is confirmed, cancelled or confirmed at an adjusted value.',
        'You agree to report completion only when the work has actually been completed, and not to move an accepted quote off the platform, or replace it with a separate arrangement with the homeowner, to avoid the success fee. If you do, we may charge the success fee on the accepted quote value and suspend your page and these tools.',
      ],
    },
    {
      id: 'statements',
      heading: '5. Statements and invoices',
      blocks: [
        'Every fee appears on your statement in the portal when it is charged, with the lead or project it relates to. Credits and adjustments appear as separate lines; we never silently change a line already on your statement.',
        'We invoice you monthly for the fees on your statement, less any credits. Payment is due within 15 days of the invoice date. Invoices are sent to the billing contact you keep up to date on the Billing page.',
        'If an invoice is not paid when due, we will tell you, and if it is still unpaid after that notice we may pause your page and stop sending you new leads until it is paid. Pausing does not cancel what you owe.',
      ],
    },
    {
      id: 'disputes',
      heading: '6. Disputes and credits',
      blocks: [
        'If you think a fee is wrong, tell us within 30 days of the date the fee appears on your statement, in the portal or by email to billing@finderlyfix.com. Include the lead or project and why you think the fee is wrong. Evidence helps: for example, messages or call records showing the contact details were not genuine, or that the work did not go ahead.',
        'We review every dispute and tell you our decision. If we agree, we add a credit to your statement for the fee or the part of it we agree is wrong.',
      ],
    },
    {
      id: 'price-changes',
      heading: '7. Changes to prices',
      blocks: [
        'We will give you at least 30 days’ notice in the portal before we increase a lead fee or the success fee percentage. The price shown when you accept a lead always governs that lead, and the success fee percentage shown when you send a quote always governs that quote.',
        'You may stop using the pay-per-lead and quote tools at any time. Fees already earned under this Schedule remain due.',
      ],
    },
    {
      id: 'taxes',
      heading: '8. Taxes and currency',
      blocks: [
        'All fees are in US dollars and exclude taxes. You are responsible for any sales, use or similar taxes on the fees, other than taxes on our income.',
      ],
    },
    {
      id: 'terms',
      heading: '9. Relationship to the Partner Terms',
      blocks: [
        'The FinderlyFix Partner Terms govern everything this Schedule does not cover, including how we handle homeowner information. If this Schedule and the Partner Terms conflict about fees, this Schedule wins.',
      ],
    },
  ],
};
